<template>
  <div v-if="isInitialized">
    <form
      v-if="hasCategory"
      class="py-4">
      <validation-observer
        ref="form"
        v-slot="observer">
        <div
          v-for="(date, index) in value.dates"
          :key="index"
          class="dates-wrapper">
          <div class="col-header">
            <label
              class="col-form-label">
              活動日
            </label>
            <button
              v-if="index > 0"
              type="button"
              class="delete-date-button"
              @click="deleteDate(index)">
              <span class="form-info__icon material-icons">
                delete
              </span>
            </button>
          </div>
          <validation-provider
            v-slot="{ errors }"
            class="col"
            name="活動日"
            rules="required|valid_date">
            <b-form-datepicker
              v-model="value.dates[index].date"
              class="align-items-center"
              placeholder="日付を選択してください"
              hide-header>
              <template v-slot:button-content>
                <div
                  class="material-icons mr-2 d-flex align-items-center">
                  event_available
                </div>
              </template>
            </b-form-datepicker>
            <p class="text-primary">
              {{ errors[0] }}
            </p>
          </validation-provider>
          <div class="row pb-2">
            <div class="col-3">
              <label
                class="col-form-label">
                終日
              </label>
              <base-switch
                v-model="value.dates[index].isAllDay"
                name="is-allday" />
            </div>
            <div
              v-if="!value.dates[index].isAllDay"
              class="col-9">
              <label
                class="col-form-label">
                活動時間
              </label>
              <div class="row justify-content-between">
                <validation-provider
                  v-slot="{ errors }"
                  :rules="`required|valid_time|before_time:@終了時間_${index}`"
                  class="col-5 pr-0"
                  :name="`開始時間_${index}`">
                  <base-timepicker
                    :value="value.dates[index].startAt"
                    max="23:58"
                    lazy
                    placeholder="時間を選択してください"
                    @input="(v) => onInputStartAt(index, v)" />
                  <p class="text-primary">
                    {{ errors[0] }}
                  </p>
                </validation-provider>
                <span class="py-2">〜</span>
                <validation-provider
                  v-slot="{ errors }"
                  :rules="`required|valid_time|after_time:@開始時間_${index}`"
                  class="col-5 pl-0"
                  :name="`終了時間_${index}`">
                  <base-timepicker
                    v-model="value.dates[index].endAt"
                    :min="minEndAt(value.dates[index].startAt)"
                    max="23:59"
                    right
                    placeholder="時間を選択してください" />
                  <p class="text-primary">
                    {{ errors[0] }}
                  </p>
                </validation-provider>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="!isEdit"
          class="add-date-button-wrapper">
          <add-button @click="addDate" />
          <label
            class="add-date-button-label"
            @click="addDate">活動日を追加</label>
        </div>
        <div>
          <b-form-group
            label="カテゴリー">
            <template v-if="isEdit">
              <validation-provider
                v-slot="{ errors }"
                rules="required"
                name="カテゴリー">
                <b-form-select
                  v-model="value.category"
                  class="category-checkboxes"
                  :options="categoryOptions"
                  stacked />
                <p class="text-primary">
                  {{ errors[0] }}
                </p>
              </validation-provider>
            </template>
            <template v-else>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
                name="カテゴリー">
                <b-form-checkbox-group
                  v-model="value.categories"
                  class="category-checkboxes"
                  :options="categoryOptions"
                  size="lg"
                  stacked />
                <p class="text-primary">
                  {{ errors[0] }}
                </p>
              </validation-provider>
            </template>
          </b-form-group>
        </div>
        <div class="d-flex">
          <label
            class="col-form-label pr-2">
            タイトル
          </label>
          <event-template-dropdown-menu
            :value="selectedTemplateId"
            text="テンプレ"
            :options="templateOptions"
            @input="selectTemplate" />
        </div>
        <div class="row px-3">
          <validation-provider
            v-slot="{ errors }"
            name="色付け"
            class="pr-0"
            rules="required">
            <b-form-input
              v-model="value.color"
              type="color"
              class="event-form__color-input"
              hide-header />
            <p class="text-primary">
              {{ errors[0] }}
            </p>
          </validation-provider>
          <validation-provider
            v-slot="{ errors }"
            name="タイトル"
            class="col pl-0 pr-0"
            rules="required">
            <b-form-input
              v-model="value.title"
              type="text"
              class="event-form__title-input"
              placeholder="タイトルを入力してください"
              hide-header />
            <p class="text-primary">
              {{ errors[0] }}
            </p>
          </validation-provider>
        </div>
        <label
          class="col-form-label">詳細</label>
        <validation-provider
          v-slot="{ errors }"
          name="詳細">
          <textarea
            ref="textarea"
            v-model="value.detail"
            type="text"
            class="form-control"
            placeholder="詳細を入力してください"
            :rows="8" />
          <p class="text-primary">
            {{ errors[0] }}
          </p>
        </validation-provider>
        <div
          v-if="attachmentOption"
          class="general-form__group form-group mt-3 mt-md-4">
          <label
            class="col-form-label">ファイルを添付</label>
          <validation-provider
            v-slot="{ errors }"
            name="添付ファイル"
            rules="file_size">
            <file-uploader
              v-model="value.file"
              :filename="filename" />
            <p class="text-primary">
              {{ errors[0] }}
            </p>
          </validation-provider>
        </div>
        <div class="general-form__btn row mt-5 justify-content-end">
          <div class="col-6">
            <button
              type="button"
              class="btn btn-block btn-cancel"
              @click="onClickCancel">
              キャンセル
            </button>
          </div>
          <div class="col-6">
            <button
              type="button"
              class="btn btn-block btn-primary"
              @click="onSubmit({ isDraft: false })">
              {{ isEdit ? '保存' : isCopy ? 'コピーから作成' : '作成' }}
            </button>
          </div>
          <div class="col-6 pt-4">
            <button
              type="button"
              class="btn btn-block btn-secondary"
              @click="onSubmit({ isDraft: true })">
              下書き保存
            </button>
          </div>
        </div>
        <div
          v-if="isEdit"
          class="general-form__item d-flex form-info justify-content-between mt-5">
          <div
            style="cursor: pointer;"
            class="form-info__delete"
            @click="onClickDelete">
            <span class="form-info__icon material-icons">
              delete
            </span>
            削除する
          </div>
          <div class="form-info__detail text-right">
            <div class="form-info__name">
              <p><b class="mr-2">作成者</b>{{ event.createdBy.name }}</p>
            </div>
            <div class="form-info__date">
              <p>最終更新日時: {{ event.updatedAt | prettyDateTime }}</p>
            </div>
          </div>
        </div>
      </validation-observer>
    </form>
    <div
      v-else>
      <h3 class="pt-10">
        カテゴリーが見つかりませんでした。
      </h3>
      <p class="mt-10 mb-4">
        予定を作成するにはまずはカテゴリーを1つ以上作成してください。
      </p>
      <router-link
        to="/admin/categories/add">
        <button
          type="button"
          class="btn btn-block btn-primary">
          カテゴリー作成
        </button>
      </router-link>
    </div>
  </div>
</template>

<script>
/*
  EventFormはデータ（event）の中身に対して責務を持つ。
  形式については親コンポーネントでフォーマットする。

  親コンポーネントはデータを
    {
      ...,
      dates: [
        {
          date,
          startAt,
          endAt,
          isAllDay
        }
      ]
    }
  の形式でEventFormへpropsで渡し、EventFormは同様の形式で返す
*/
import { mapState, mapGetters } from 'vuex'
import { format, formatISO, endOfDay, startOfDay } from 'date-fns'
import cloneDeep from 'lodash/cloneDeep'
import { getRoundedAnHour, getNowTimeDate } from '@/utils/datetime'
import FileUploader from '@/components/parts/FileUploader'
import BaseSwitch from '@/components/parts/BaseSwitch.vue'
import BaseTimepicker from '@/components/parts/BaseTimepicker.vue'
import EventTemplateDropdownMenu from '@/components/view/EventTemplateDropdownMenu.vue'
import AddButton from '@/components/parts/AddButton.vue'
import { fileToBase64 } from '@/utils/file'

export default {
  components: {
    EventTemplateDropdownMenu,
    FileUploader,
    BaseSwitch,
    BaseTimepicker,
    AddButton
  },
  props: {
    isEdit: {
      type: Boolean,
      default: false
    },
    isCopy: {
      type: Boolean,
      default: false
    },
    /**
     * 新規の場合はnull
     * コピーの場合はコピー元の予定
     * 変更の場合は変更元の予定が渡される
     */
    event: {
      type: Object,
      default: null,
    }
  },
  data() {
    return {
      value: {
        dates: [],
        title: null,
        detail: null,
        category: null,
        categories: [],
        color: '#D7DFE4',
        file: null,
      },
      categories: [],
      isInitialized: false,
      templates: [],
      selectedTemplateId: null
    }
  },
  computed: {
    ...mapGetters('tenant', ['attachmentOption']),
    ...mapState('events', ['selectedCategories']),
    _event: {
      get() {
        return this.event
      },
      set(v) {
        this.$emit('input', v)
      }
    },
    categoryOptions() {
      const _categories = this.categories.filter(category => this.selectedCategories.includes(category.id))
      return _categories.map(category => ({ text: category.name, value: category.id }))
    },
    templateOptions() {
      const filteredTemplates = this.templates.filter(template => {
        // カテゴリー：なしは常に表示
        if (!template.category) {
          return true
        }
        // 表示選択されたカテゴリーは表示
        if (this.selectedCategories.includes(template.category.id)) {
          return true
        }
        return false
      })

      return filteredTemplates.map(template => (
        { 
          text: template.title, 
          categoryName: template.category ? template.category.name : null,
          value: template.id, 
          color: template.color 
        }
      ))
    },
    hasCategory() {
      return this.categories.length > 0
    },
    hasQueryDate() {
      return !!this.$route.query.date
    },
    baseDate() {
      // 現在時刻から10分刻みの時刻に修正
      const date = this.hasQueryDate ? getNowTimeDate(new Date(this.$route.query.date)) : new Date()
      const { startAt, endAt } = getRoundedAnHour(date)

      // Date形式のデータを11:00のように変換
      return {
        date,
        startAt: this.formatHHmm(new Date(startAt)),
        endAt: this.formatHHmm(new Date(endAt)),
        isAllDay: false
      }
    },
    filename() {
      // 新しくファイルを添付している場合はそのファイル名を返す
      if (this.value.file instanceof File) {
        return this.value.file.name
      }
      return this.value.filename
    }
  },
  async created() {
    this.$store.commit('startLoading')

    // 予定作成時にbaseDateをdatesに設定
    if (this.value.dates.length === 0) {
      this.value.dates = [{ ...this.baseDate }]
    }

    // 編集またはコピーから作成する場合はフォームに値をセット
    if (this.isEdit || this.isCopy) {
      this.setFormValueFromEvent()
    }

    const promises = [this.fetchTenantInfo(), this.fetchTemplates(), this.fetchCategories()]
    await Promise.all(promises)
    this.isInitialized = true
    this.$store.commit('finishLoading')
  },
  methods: {
    setFormValueFromEvent() {
      const {
        color,
        title,
        detail,
        startAt,
        endAt,
        isAllDay,
        category,
        file,
        filename,
      } = this.event

      this.value = {
        ...this.value,
        color,
        title,
        detail,
        category: category.id,
        categories: [category.id],
        file: this.isEdit ? file : null,
        filename: this.isEdit ? filename : null,
        dates: [
          {
            date: startAt,
            startAt: this.formatHHmm(new Date(startAt)),
            endAt: this.formatHHmm(new Date(endAt)),
            isAllDay,
          }
        ]
      }
    },
    minEndAt(_startAt) {
      if (!_startAt) {
        return '00:01'
      }
      const [startAtHH, startAtMm] = _startAt.split(':')
      if (startAtMm === '59') {
        return `${+startAtHH + 1}:00`
      }
      const minEndAtMm = `${+startAtMm + 1}`.padStart(2, '0')
      return `${startAtHH}:${minEndAtMm}`
    },
    hhmmToISOString(_date, _time, _isAllDay, _ofDayFunc) {
      const time = new Date(_date)
      if (_isAllDay) {
        return formatISO(_ofDayFunc(time))
      }
      const [HH, mm] = _time.split(':')
      time.setHours(HH)
      time.setMinutes(mm)
      return formatISO(time)
    },
    async fetchTenantInfo() {
      const tenantInfo = await this.$axios.get('/tenants/')
        .then((res) => {
          return res.data
        })
        .catch((e) => {
          console.error(e)
          this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
        })
      if (!tenantInfo) return
      this.$store.commit('tenant/setTenantInfo', tenantInfo)
    },
    async fetchTemplates() {
      this.templates = await this.$axios.get('/events/templates/', {
        params: {
          ordering: 'sort_order'
        }
      }).then(res => res.data.results)
    },
    formatHHmm(date) {
      return format(date, 'HH:mm')
    },
    onClickCancel() {
      this.$router.back()
    },
    async fetchCategories() {
      this.categories = await this.$axios.get('/events/categories/', {
        params: {
          ordering: 'sort_order'
        }
      }).then(res => res.data.results)
    },
    onClickDelete() {
      if (!window.confirm(`${this.event.title}を削除してもよろしいですか?`)) return
      this.deleteEvent().then(() => {
        const formattedDate = format(new Date(this.event.startAt), 'yyyy-MM-dd')
        this.$router.push(`/events/?date=${formattedDate}`)
        this.$store.commit('notify/set', {message: `${this.event.title}を削除しました。`})
      }).catch(e => {
        console.error(e)
        this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
      })
    },
    deleteEvent() {
      return this.$axios.delete(`events/${this.event.id}/`)
    },
    selectTemplate(templateId) {
      const template = this.templates.find(template => template.id === templateId)
      if (!template) {
        this.$store.commit('notify/set', {message: 'テンプレートが見つかりません', type: 'danger'})
        return
      }
      const { title, detail, color } = template
      this.value = {
        ...this.value,
        title,
        detail,
        color
      }
      this.selectedTemplateId = templateId
    },
    async onSubmit({ isDraft }) {
      const isValid = await this.$refs.form.validate()
      if (!isValid) {
        this.$store.commit('notify/set', {message: '入力内容に不備があります。ご確認の上再度実行してください。', type: 'danger'})
        return
      }
      const requestBody = await this.getRequestBody(isDraft)

      this.$emit('submit', requestBody)
    },
    /**
     * 作成と更新で出力する形を変える
     */
    async getRequestBody(isDraft) {
      const file = this.value.file
      // fileがnullの場合は削除、Fileの場合はファイルを添付、それ以外の場合はundefinedにし、変更なしにする
      const base64File = file === null ? null : file instanceof File ? await fileToBase64(file) : undefined
      const filename = file === null ? null : file instanceof File ? this.value.file.name : undefined
      // 更新時のリクエストボディを生成
      if (this.isEdit) {
        const { dates, color, category, title, detail } = this.value
        return {
          startAt: this.hhmmToISOString(dates[0].date, dates[0].startAt, dates[0].isAllDay, startOfDay),
          endAt: this.hhmmToISOString(dates[0].date, dates[0].endAt, dates[0].isAllDay, endOfDay),
          isAllDay: dates[0].isAllDay,
          color,
          category,
          title,
          detail,
          file: base64File,
          filename,
          isDraft
        }
      }
      // 作成時のリクエストボディを生成
      const dates = this.value.dates.map(date => ({
        startAt: this.hhmmToISOString(date.date, date.startAt, date.isAllDay, startOfDay),
        endAt: this.hhmmToISOString(date.date, date.endAt, date.isAllDay, endOfDay),
        isAllDay: date.isAllDay
      }))
      return {
        ...this.value,
        dates,
        file: base64File,
        filename,
        isDraft,
      }
    },
    addDate() {
      // 最後の日付をコピーして追加する
      const newDate = cloneDeep(this.value.dates[this.value.dates.length - 1])
      this.value.dates = [...this.value.dates, newDate]
    },
    deleteDate(index) {
      this.value.dates.splice(index, 1)
    },
    /**
     * StartAtが変更された時、EndAtをStartAtの１時間後に設定する
     */
    onInputStartAt(index, v) {
      this.value.dates[index].startAt = v
      let [startAtHH, startAtMm] = v.split(':')
      startAtHH = Number(startAtHH)
      if (startAtHH === 23) {
        this.value.dates[index].endAt = '23:59'
      } else {
        const endAtHH = String(startAtHH + 1)
        const paddedEndAtHH = endAtHH.padStart(2, '0')
        this.value.dates[index].endAt = `${paddedEndAtHH}:${startAtMm}`
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.event-form {
  &__color-input {
    width: 30px;
    height: 48px !important;
    border-radius: 0.25rem 0 0 0.25rem;
  }

  &__title-input {
    border-radius: 0 0.25rem 0.25rem 0;
  }
}

.form-info {
  color: $grey09;
  &__delete{
    text-decoration: none;
    font-weight: bold;
    color: $grey09;
  }
  &__icon{
    display: inline-block;
    vertical-align: top;
  }
  &__date{
    font-size: $fs-m;
  }
}

.col-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: -22px;

  .delete-date-button {
    border: none;
    background: none;
    color: $grey12;
    font-size: $fs-m;
  }
}

.add-date-button-wrapper {
  display: flex;
  align-items: center;
  margin-bottom: 40px;

  .add-date-button-label {
    margin: 0 0 0 15px;
  }
}
</style>

<style lang="scss">
.category-checkboxes {
  .custom-control {
    &-label {
      font-size: 16px;
      // チェックボックスの位置調整
      &::before, &::after {
        top: 0.125rem !important;
      }
    }
  }
}
</style>